/**
 * @file stage-form.ts
 * @description Модель формы стадии воронки (phase09; phase19.31 — вынесено
 * из `StageEditDialog`, чтобы окно уложилось в 200 строк): значения полей,
 * набор цветов-меток, заполнение из стадии и проверка перед сохранением.
 *
 * Набор цветов фиксированный (SC28: цвет магазина — только точкой-меткой из
 * набора); переход на новые метки — этап 2, здесь набор прежний.
 */

import type { CreateStagePayload, PipelineStageDto, StageKind } from "../../../lib/api-crm";

/** Виды стадий в порядке выбора. */
export const STAGE_KINDS: StageKind[] = ["INITIAL", "IN_PROGRESS", "WON", "LOST", "CANCELLED", "ON_HOLD"];

/** Цвета-метки стадии. */
export const STAGE_PALETTE = ["#5a6b36", "#9e8b5a", "#c0a890", "#fbecce", "#7d8c4f", "#9e9483", "#c95a4f", "#3d4b1f"] as const;

export interface StageFormValue {
  code: string;
  name: string;
  kind: StageKind;
  color: string;
  /** Срок стадии в часах строкой поля; пусто — без срока. */
  slaHours: string;
  isActive: boolean;
}

/** Поля формы: из стадии при правке, по умолчанию при создании. */
export function stageFormFrom(stage: PipelineStageDto | undefined): StageFormValue {
  return {
    code: stage?.code ?? "",
    name: stage?.name ?? "",
    kind: stage?.kind ?? "IN_PROGRESS",
    color: stage?.color ?? STAGE_PALETTE[0],
    slaHours: stage?.slaHours ? String(stage.slaHours) : "",
    isActive: stage?.isActive ?? true,
  };
}

export type StageFormCheck = { ok: true; payload: CreateStagePayload } | { ok: false; error: string };

/** Проверка формы: человеческий текст ошибки или готовые данные для сервера. */
export function checkStageForm(form: StageFormValue): StageFormCheck {
  const code = form.code.trim();
  if (!code) return { ok: false, error: "Укажите код стадии: латиница, цифры и _." };
  if (!/^[a-z0-9_]+$/.test(code)) return { ok: false, error: "Код: только строчная латиница, цифры и _." };
  const name = form.name.trim();
  if (!name) return { ok: false, error: "Укажите название стадии." };
  const sla = form.slaHours.trim() ? Number.parseInt(form.slaHours, 10) : undefined;
  if (sla !== undefined && (Number.isNaN(sla) || sla < 0)) {
    return { ok: false, error: "Срок стадии в часах — целое число не меньше нуля." };
  }
  return { ok: true, payload: { code, name, kind: form.kind, color: form.color, slaHours: sla } };
}
